import { Currency } from "../utils/Currency";
import { v4 as uuidv4 } from "uuid";
import { DateUtil } from "../utils/DateUtil";
import { LocalDate } from "@js-joda/core";
export class RefundRecord {
    /** Primary key */
    id;
    _amount;
    jsAmount;
    _currency;
    jsCurrency;
    _createdDate;
    jsCreatedDate;
    _effectiveDate;
    jsEffectiveDate;
    _metadata;
    _active = true;
    /** audit / versioning */
    _versionId = uuidv4();
    _dateChanged = LocalDate.now();
    initialized = false;
    /** back-pointer is set automatically when you add the refund to a DepositRecord */
    depositRecord; // typed late to avoid circular import problems
    /* ------------------------------------------------------------------ */
    constructor(params) {
        this.id = params.id ?? RefundRecord.generateUniqueId();
        if (params.active !== undefined) {
            this.active = params.active;
        }
        this.amount = Currency.of(params.amount);
        this.currency = params.currency;
        this.createdDate = params.createdDate ?? LocalDate.now();
        this.effectiveDate = params.effectiveDate;
        this.metadata = params.metadata;
        this.updateJsValues();
        this.initialized = true;
    }
    /* ------------------------------------------------------------------ */
    /*  Versioning helpers                                                */
    /* ------------------------------------------------------------------ */
    get versionId() {
        return this._versionId;
    }
    get dateChanged() {
        return this._dateChanged;
    }
    versionChanged() {
        if (!this.initialized)
            return;
        this._dateChanged = LocalDate.now();
        this._versionId = uuidv4();
        /* bubble up so the loan snapshot knows something changed */
        if (this.depositRecord?.versionChanged) {
            this.depositRecord.versionChanged();
        }
    }
    /* ------------------------------------------------------------------ */
    /*  Core properties                                                    */
    /* ------------------------------------------------------------------ */
    /** True → refund reduces deposit.balance;
     *  False → cash is “given back”, so deposit.balance increases. */
    get active() {
        return this._active;
    }
    set active(value) {
        if (value === this._active)
            return; // no change
        /* when we already belong to a deposit, move the cash in/out */
        if (this.depositRecord) {
            const delta = value ? this.amount.negated() : this.amount; // on ⇢ –amount, off ⇢ +amount
            this.depositRecord.amount = this.depositRecord.amount.add(delta);
        }
        this._active = value;
        this.versionChanged(); // bump version + bubble up
    }
    get amount() {
        return this._amount;
    }
    set amount(value) {
        this._amount = value;
        this.jsAmount = value.toNumber();
        this.versionChanged();
    }
    get currency() {
        return this._currency;
    }
    set currency(value) {
        this._currency = value;
        this.jsCurrency = value;
        this.versionChanged();
    }
    get createdDate() {
        return this._createdDate;
    }
    set createdDate(value) {
        this._createdDate = DateUtil.normalizeDate(value);
        this.jsCreatedDate = DateUtil.normalizeDateToJsDate(this._createdDate);
        this.versionChanged();
    }
    get effectiveDate() {
        return this._effectiveDate;
    }
    set effectiveDate(value) {
        const d = DateUtil.normalizeDate(value);
        this._effectiveDate = d;
        this.jsEffectiveDate = DateUtil.normalizeDateToJsDate(d);
        this.versionChanged();
    }
    get metadata() {
        return this._metadata;
    }
    set metadata(value) {
        this._metadata = value;
        this.versionChanged();
    }
    /* ------------------------------------------------------------------ */
    /*  (De)serialization helpers                                          */
    /* ------------------------------------------------------------------ */
    updateModelValues() {
        this.amount = Currency.of(this.jsAmount ?? 0);
        this.currency = this.jsCurrency ?? "";
        this.createdDate = this.jsCreatedDate;
        this.effectiveDate = this.jsEffectiveDate;
    }
    updateJsValues() {
        this.jsAmount = this.amount.toNumber();
        this.jsCurrency = this.currency;
        this.jsCreatedDate = DateUtil.normalizeDateToJsDate(this.createdDate);
        this.jsEffectiveDate = DateUtil.normalizeDateToJsDate(this.effectiveDate);
    }
    toJSON() {
        return this.json;
    }
    get json() {
        return {
            id: this.id,
            amount: this.amount.toNumber(),
            currency: this.currency,
            createdDate: DateUtil.normalizeDateToJsDate(this.createdDate),
            effectiveDate: DateUtil.normalizeDateToJsDate(this.effectiveDate),
            metadata: this.metadata,
            active: this.active,
        };
    }
    static rehydrateFromJSON(json) {
        return new RefundRecord(json);
    }
    toCode() {
        return `new RefundRecord({
  id: "${this.id}",
  amount: ${this.amount.toNumber()},
  currency: "${this.currency}",
  createdDate: DateUtil.normalizeDate("${this.createdDate.toString()}"),
  effectiveDate: DateUtil.normalizeDate("${this.effectiveDate.toString()}"),
  metadata: ${JSON.stringify(this.metadata)},
  active: ${this.active},
})`;
    }
    static generateUniqueId() {
        return "REFUND-" + Math.random().toString(36).substr(2, 9);
    }
}
